// report_builder/ExitBuilder.jsx
// ============================================================
// EXIT BUILDER
// Reusable condition block used for both Exit Conditions
// and Stop Loss Conditions in ReportBuilder.jsx.
//
// Identical to the entry condition block in functionality —
// uses the same ConditionRow component.
// Additional variables available: ENTRY_CANDLE, ENTRY_PRICE
// (these appear automatically from the registry).
//
// Props:
//   title        — section title string e.g. "Exit Conditions"
//   subtitle     — optional hint text below title
//   conditions   — array of condition objects
//   onChange     — fn(updatedConditions) called on any change
//   registry     — variable + operator registry from API
//   enabled      — bool — if false shows enable toggle only
//   onToggle     — fn(bool) — called when enabled toggled
// ============================================================

import React, { useCallback } from 'react';
import ConditionRow, { defaultCondition } from './ConditionRow';

let _nextId = 500;
const nextId = () => ++_nextId;


export default function ExitBuilder({
  title,
  subtitle,
  conditions,
  onChange,
  registry,
  enabled,
  onToggle,
}) {

  // ── Condition handlers ────────────────────────────────────

  const handleConditionChange = useCallback((index, updated) => {
    const next = [...conditions];
    next[index] = updated;
    onChange(next);
  }, [conditions, onChange]);

  const handleDelete = useCallback((index) => {
    if (conditions.length <= 1) {
      // Last condition deleted — disable the block entirely
      onToggle(false);
      onChange([{ ...defaultCondition(nextId()), connector: null }]);
      return;
    }
    const next = conditions.filter((_, i) => i !== index);
    if (next.length > 0) next[0] = { ...next[0], connector: null };
    onChange(next);
  }, [conditions, onChange, onToggle]);

  const handleConnector = useCallback((index, value) => {
    const next = [...conditions];
    next[index] = { ...next[index], connector: value };
    onChange(next);
  }, [conditions, onChange]);

  const handleAddCondition = () => {
    onChange([
      ...conditions,
      { ...defaultCondition(nextId()), connector: 'AND' },
    ]);
  };

  const handleAddNWrapper = () => {
    onChange([
      ...conditions,
      {
        id:        nextId(),
        type:      'n_wrapper',
        connector: 'AND',
        mode:      'any',
        n:         5,
        inner: {
          type:     'comparison',
          left:     { variable: 'CLOSE', params: { timeframe: 'auto', offset: 0 } },
          operator: 'lt',
          right:    { variable: 'ENTRY_PRICE', params: {} },
        },
      },
    ]);
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={s.section}>

      {/* ── Section header with enable toggle ── */}
      <div style={s.sectionHeader}>
        <div style={s.titleRow}>
          <span style={s.sectionTitle}>{title}</span>
          {subtitle && !enabled && (
            <span style={s.subtitle}>{subtitle}</span>
          )}
        </div>

        {/* Enable / Disable toggle */}
        <div style={s.toggleRow}>
          <span style={s.toggleLabel}>
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
          <div
            style={{ ...s.toggleSwitch, ...(enabled ? s.toggleSwitchOn : {}) }}
            onClick={() => onToggle(!enabled)}
          >
            <div style={{
              ...s.toggleThumb,
              ...(enabled ? s.toggleThumbOn : {}),
            }} />
          </div>
        </div>
      </div>

      {/* ── Disabled state ── */}
      {!enabled && (
        <div style={s.disabledHint}>
          {subtitle || `Enable to add ${title.toLowerCase()}.`}
          {' '}Report will run without {title.toLowerCase()}.
        </div>
      )}

      {/* ── Condition builder (only when enabled) ── */}
      {enabled && (
        <>
          {/* Add buttons */}
          <div style={s.addBtns}>
            <button style={s.btnAdd} onClick={handleAddCondition}>
              + Condition
            </button>
            <button style={s.btnAddWrapper} onClick={handleAddNWrapper}>
              + Any / All
            </button>
          </div>

          {/* Hint for ENTRY_CANDLE / ENTRY_PRICE */}
          <div style={s.entryVarHint}>
            💡 Use <strong>Entry Candle</strong> and <strong>Entry Price</strong>{' '}
            variables to reference the trade entry candle.
          </div>

          {/* Condition rows */}
          <div style={s.conditionList}>
            {conditions.map((cond, idx) => (
              <ConditionRow
                key={cond.id ?? idx}
                condition={cond}
                index={idx}
                registry={registry}
                onChange={handleConditionChange}
                onDelete={handleDelete}
                onConnector={handleConnector}
                isFirst={idx === 0}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  section: {
    background:   '#fff',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    padding:      '12px 14px',
  },
  sectionHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   '8px',
  },
  titleRow: {
    display:    'flex',
    alignItems: 'baseline',
    gap:        '8px',
  },
  sectionTitle: {
    fontSize:      '12px',
    fontWeight:    'bold',
    color:         '#444',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  subtitle: {
    fontSize: '11px',
    color:    '#aaa',
  },
  toggleRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '6px',
  },
  toggleLabel: {
    fontSize: '11px',
    color:    '#888',
  },
  toggleSwitch: {
    width:        '36px',
    height:       '20px',
    borderRadius: '10px',
    background:   '#ddd',
    cursor:       'pointer',
    position:     'relative',
    transition:   'background 0.2s',
    flexShrink:   0,
  },
  toggleSwitchOn: {
    background: '#2962ff',
  },
  toggleThumb: {
    position:     'absolute',
    top:          '3px',
    left:         '3px',
    width:        '14px',
    height:       '14px',
    borderRadius: '50%',
    background:   '#fff',
    transition:   'left 0.2s',
    boxShadow:    '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleThumbOn: {
    left: '19px',
  },
  disabledHint: {
    fontSize:  '12px',
    color:     '#bbb',
    padding:   '4px 0',
  },
  addBtns: {
    display:      'flex',
    gap:          '6px',
    marginBottom: '8px',
  },
  btnAdd: {
    padding:         '4px 10px',
    fontSize:        '12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  btnAddWrapper: {
    padding:         '4px 10px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  entryVarHint: {
    fontSize:     '11px',
    color:        '#888',
    background:   '#fffbf0',
    border:       '1px solid #ffe082',
    borderRadius: '3px',
    padding:      '4px 8px',
    marginBottom: '8px',
  },
  conditionList: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
};